import { useEffect, useRef, useState } from "react";
import { useAlert } from "context/AlertContext";
import apiKnowledgeHub from "../api";

const bump = (value, delta) => Math.max(0, (value || 0) + delta);

// Optimistic flip — sets the viewer's own like state and moves every counter
// that mirrors it by one, so the UI settles before the request is even sent.
const applyLikeFlip = (article, contentId, isLiked) => {
  if (!article) return article;
  const delta = isLiked ? 1 : -1;

  return {
    ...article,
    engagement: { ...article.engagement, total_likes: bump(article.engagement?.total_likes, delta) },
    kb_contents: article.kb_contents.map((block) =>
      block.id !== contentId
        ? block
        : {
            ...block,
            viewer_state: { ...block.viewer_state, is_liked: isLiked },
            engagement: { ...block.engagement, total_likes: bump(block.engagement?.total_likes, delta) },
            likes_preview: {
              ...block.likes_preview,
              total_likes: bump(block.likes_preview?.total_likes ?? block.engagement?.total_likes, delta),
            },
          }
    ),
  };
};

// Server truth — absolute counts, so likes other readers added while this
// request was in flight are folded in rather than lost.
const applyLikeTotals = (article, contentId, { is_liked, content_total_likes, article_total_likes }) => {
  if (!article) return article;

  return {
    ...article,
    engagement: { ...article.engagement, total_likes: article_total_likes },
    kb_contents: article.kb_contents.map((block) =>
      block.id !== contentId
        ? block
        : {
            ...block,
            viewer_state: { ...block.viewer_state, is_liked },
            engagement: { ...block.engagement, total_likes: content_total_likes },
            likes_preview: { ...block.likes_preview, total_likes: content_total_likes },
          }
    ),
  };
};

// Shared "load a full interactive article + act on it" logic for anywhere
// that detail is shown — the client reader page (ArticleView) and the admin
// CRUD article-list's preview dialog alike, since both read and write
// through the exact same knowledge_hub API and patch the same shapes back
// into local state (instant, no refetch, never disturbs scroll position or
// which block's comment panel is open).
//
// The hook owns state + actions only; the caller decides *when* to fetch
// (on mount for a page, on dialog open for a modal) via the exposed
// fetchArticle().
export default function useArticleInteractions(articleId) {
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [article, setArticle] = useState(null);

  // Optimistic handlers need the *current* value at click time to know which
  // way to flip, without taking `article` as a dependency on every render.
  const articleRef = useRef(null);
  useEffect(() => {
    articleRef.current = article;
  }, [article]);

  // Monotonic ticket per action. A response may only reconcile state if no
  // newer click has happened since it was sent — otherwise a slow first
  // response would clobber what a fast second click already established.
  const ticketRef = useRef({});
  const takeTicket = (key) => {
    const ticket = (ticketRef.current[key] || 0) + 1;
    ticketRef.current[key] = ticket;
    return () => ticketRef.current[key] === ticket;
  };

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const res = await apiKnowledgeHub.getArticle(articleId);
      if (res.data.status) {
        setArticle(res.data.data);
      } else {
        showAlert({ type: "error", message: res.data?.message || "Failed to load article" });
      }
    } catch (error) {
      showAlert({ type: "error", message: error.response?.data?.message || "Failed to load article" });
    } finally {
      setLoading(false);
    }
  };

  // Bookmark / pin / like are all optimistic: paint the new state on click,
  // send the request in the background, then reconcile with the server's
  // absolute numbers (or roll the flip back if it failed). Waiting on the
  // response first made every one of these feel like a 3-5s dead click.
  const handleToggleBookmark = async () => {
    const nextBookmarked = !articleRef.current?.viewer_state?.is_bookmarked;
    const isCurrent = takeTicket("bookmark");

    setArticle((prev) =>
      !prev
        ? prev
        : {
            ...prev,
            viewer_state: { ...prev.viewer_state, is_bookmarked: nextBookmarked },
            engagement: {
              ...prev.engagement,
              total_bookmarks: bump(prev.engagement?.total_bookmarks, nextBookmarked ? 1 : -1),
            },
          }
    );

    try {
      const res = await apiKnowledgeHub.toggleBookmark(articleId);
      if (!isCurrent()) return;
      if (!res.data.status) throw new Error(res.data?.message);

      setArticle((prev) =>
        !prev
          ? prev
          : {
              ...prev,
              viewer_state: { ...prev.viewer_state, is_bookmarked: res.data.data.is_bookmarked },
              engagement: { ...prev.engagement, total_bookmarks: res.data.data.total_bookmarks },
            }
      );
    } catch (error) {
      if (!isCurrent()) return;
      setArticle((prev) =>
        !prev
          ? prev
          : {
              ...prev,
              viewer_state: { ...prev.viewer_state, is_bookmarked: !nextBookmarked },
              engagement: {
                ...prev.engagement,
                total_bookmarks: bump(prev.engagement?.total_bookmarks, nextBookmarked ? -1 : 1),
              },
            }
      );
      showAlert({ type: "error", message: error.response?.data?.message || "Something went wrong" });
    }
  };

  const handleTogglePin = async () => {
    const nextPinned = !articleRef.current?.viewer_state?.is_pinned;
    const isCurrent = takeTicket("pin");

    setArticle((prev) => (!prev ? prev : { ...prev, viewer_state: { ...prev.viewer_state, is_pinned: nextPinned } }));

    try {
      const res = await apiKnowledgeHub.togglePin(articleId);
      if (!isCurrent()) return;
      if (!res.data.status) throw new Error(res.data?.message);

      setArticle((prev) =>
        !prev ? prev : { ...prev, viewer_state: { ...prev.viewer_state, is_pinned: res.data.data.is_pinned } }
      );
    } catch (error) {
      if (!isCurrent()) return;
      setArticle((prev) =>
        !prev ? prev : { ...prev, viewer_state: { ...prev.viewer_state, is_pinned: !nextPinned } }
      );
      showAlert({ type: "error", message: error.response?.data?.message || "Something went wrong" });
    }
  };

  const handleToggleLike = async (contentId) => {
    const block = articleRef.current?.kb_contents?.find((item) => item.id === contentId);
    if (!block) return;

    const nextLiked = !block.viewer_state?.is_liked;
    const isCurrent = takeTicket(`like:${contentId}`);

    setArticle((prev) => applyLikeFlip(prev, contentId, nextLiked));

    try {
      const res = await apiKnowledgeHub.toggleLike(contentId);
      if (!isCurrent()) return;
      if (!res.data.status) throw new Error(res.data?.message);

      setArticle((prev) => applyLikeTotals(prev, contentId, res.data.data));
    } catch (error) {
      if (!isCurrent()) return;
      setArticle((prev) => applyLikeFlip(prev, contentId, !nextLiked));
      showAlert({ type: "error", message: error.response?.data?.message || "Something went wrong" });
    }
  };

  const handleAddComment = async (contentId, text, parentFid) => {
    try {
      const res = await apiKnowledgeHub.addComment(contentId, { comment: text, parent_fid: parentFid });
      if (!res.data.status) {
        showAlert({ type: "warning", message: res.data?.message });
        return;
      }
      const created = res.data.data;
      const newComment = {
        id: created.id,
        comment: created.comment,
        created_date: created.created_date,
        updated_date: null,
        is_pinned: false,
        pinned_at: null,
        user: created.user,
        reply_count: 0,
        replies: [],
      };

      setArticle((prev) => ({
        ...prev,
        engagement: { ...prev.engagement, total_comments: created.article_total_comments },
        kb_contents: prev.kb_contents.map((block) => {
          if (block.id !== contentId) return block;
          const items = parentFid
            ? (block.comments?.items || []).map((c) =>
                c.id === parentFid
                  ? { ...c, reply_count: (c.reply_count || 0) + 1, replies: [newComment, ...(c.replies || [])] }
                  : c
              )
            : [...(block.comments?.items || []), newComment];

          return {
            ...block,
            comments: {
              total_comments: created.content_total_comments,
              top_level_count: parentFid ? block.comments?.top_level_count || 0 : (block.comments?.top_level_count || 0) + 1,
              items,
            },
          };
        }),
      }));
    } catch (error) {
      showAlert({ type: "error", message: error.response?.data?.message || "Failed to post comment" });
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const res = await apiKnowledgeHub.deleteComment(commentId);
      if (!res.data.status) {
        showAlert({ type: "warning", message: res.data?.message });
        return;
      }
      const { content_id, content_total_comments, article_total_comments } = res.data.data;

      setArticle((prev) => ({
        ...prev,
        engagement: { ...prev.engagement, total_comments: article_total_comments },
        kb_contents: prev.kb_contents.map((block) => {
          if (block.id !== content_id) return block;
          const items = (block.comments?.items || [])
            .filter((c) => c.id !== commentId)
            .map((c) => ({ ...c, replies: (c.replies || []).filter((r) => r.id !== commentId) }));

          return {
            ...block,
            comments: { total_comments: content_total_comments, top_level_count: items.length, items },
          };
        }),
      }));
    } catch (error) {
      showAlert({ type: "error", message: error.response?.data?.message || "Failed to delete comment" });
    }
  };

  // ADMIN/SUPER_ADMIN only — the "Pin comment" control itself is only ever
  // rendered when article.can_moderate is true, and the backend re-checks
  // the role regardless of what's sent here.
  const handlePinComment = async (commentId) => {
    try {
      const res = await apiKnowledgeHub.pinComment(commentId);
      if (!res.data.status) {
        showAlert({ type: "warning", message: res.data?.message });
        return;
      }
      const { is_pinned } = res.data.data;

      setArticle((prev) => ({
        ...prev,
        kb_contents: prev.kb_contents.map((block) => {
          if (!block.comments?.items?.some((c) => c.id === commentId)) return block;
          return {
            ...block,
            comments: {
              ...block.comments,
              items: block.comments.items.map((c) =>
                c.id === commentId ? { ...c, is_pinned, pinned_at: is_pinned ? new Date().toISOString() : null } : c
              ),
            },
          };
        }),
      }));
    } catch (error) {
      showAlert({ type: "error", message: error.response?.data?.message || "Something went wrong" });
    }
  };

  return {
    loading,
    article,
    fetchArticle,
    handleToggleBookmark,
    handleTogglePin,
    handleToggleLike,
    handleAddComment,
    handleDeleteComment,
    handlePinComment,
  };
}
